import clsx from "clsx";
import { type ReactNode, useId } from "react";

import { Disclosure, DisclosureChevron } from "../components/panel";
import { Motion, Presence } from "../components/presence";
import { StepMark } from "../components/StepMark";
import { Badge } from "../components/ui";
import { t } from "../i18n/i18n";
import { type Fold, type RailState, railLabel, type Step, type StepBadgeTone } from "./missionSteps";

/** Parcours guidé des Données initiales (refonte du 02/11/2026, « cela donne l'impression d'un grand fouillis,
 *  vraiment pas clair ») : un rail de progression en haut du panneau, puis un accordéon où un seul volet est ouvert
 *  à la fois — les cinq étapes, puis les options facultatives. La logique (étape ouverte, validation, repères) est
 *  dans `missionSteps.ts` ; ici, le rendu seul.
 *
 *  Volet replié : une ligne cliquable — pastille, titre, résumé en une ligne lisible et « Modifier ». Volet ouvert :
 *  ses champs seuls, dépliés en hauteur et en fondu (`Disclosure`, contenu toujours monté : météo, lieu nommé et
 *  drones gardent leurs effets). `data-fold` : volet que `designate()` fait ouvrir (événement `REVEAL_EVENT`) quand
 *  sa cible y est cachée. */

/** Rail de progression : cinq repères cliquables (coche faite, anneau accent en cours, anneau gris à faire), reliés
 *  par un filet, libellés courts. Collé en haut de la zone qui défile. */
export function StepsRail({
  steps,
  state,
  label,
  onPick,
}: {
  steps: readonly Step[];
  state: (step: Step) => RailState;
  /** Nom de la liste pour les lecteurs d'écran (« Étapes faites : 2 sur 5 »). */
  label: string;
  onPick: (step: Step) => void;
}) {
  return (
    <nav aria-label={label} data-steps-rail="" className="sticky top-0 z-10 border-b border-line bg-surface px-3 pt-2 pb-2.5">
      <ol className="relative grid" style={{ gridTemplateColumns: `repeat(${steps.length}, minmax(0, 1fr))` }}>
        {/* Filet d'un repère à l'autre, d'un centre au suivant (sous les pastilles). */}
        <span aria-hidden className="absolute top-3.5 h-px bg-line" style={{ left: `${50 / steps.length}%`, right: `${50 / steps.length}%` }} />
        {steps.map((step) => {
          const now = state(step);
          return (
            <li key={step.key} className="relative min-w-0">
              <button
                type="button"
                onClick={() => onPick(step)}
                aria-current={now === "current" ? "step" : undefined}
                className="focus-inset flex w-full min-w-0 flex-col items-center gap-1 rounded-lg px-0.5 py-1 transition-colors duration-(--gx-enter) hover:bg-hover"
              >
                <span className="rounded-full bg-surface p-0.5">
                  <StepMark
                    state={now === "current" ? "next" : now === "done" ? "done" : "pending"}
                    n={step.n}
                    total={steps.length}
                  />
                </span>
                <span
                  className={clsx(
                    "max-w-full text-center text-meta leading-tight break-words transition-colors duration-(--gx-enter)",
                    now === "current" ? "font-medium text-ink" : "text-ink-muted",
                  )}
                >
                  {railLabel(step)}
                </span>
              </button>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

/** Amène un volet juste sous le rail, dans la zone qui défile du panneau. Défilement calculé sur cette zone seule :
 *  `scrollIntoView` ferait aussi défiler les enveloppes `overflow-hidden` du panneau pendant l'animation de sa
 *  hauteur, et le rail sortirait du cadre. */
export function scrollFoldIntoView(section: HTMLElement | null | undefined) {
  if (!section) return;
  let scroller = section.parentElement;
  while (scroller && !/(auto|scroll)/.test(getComputedStyle(scroller).overflowY)) scroller = scroller.parentElement;
  if (!scroller) return;
  const rail = scroller.querySelector<HTMLElement>("[data-steps-rail]")?.offsetHeight ?? 0;
  const top = section.getBoundingClientRect().top - scroller.getBoundingClientRect().top + scroller.scrollTop - rail;
  let reduced = false;
  try {
    reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  } catch {
    reduced = false;
  }
  scroller.scrollTo({ top: Math.max(0, top), behavior: reduced ? "auto" : "smooth" });
}

/** Volet de l'accordéon (une étape, ou les options facultatives). */
export function FoldSection({
  fold,
  open,
  mark,
  title,
  summary,
  badge,
  editable,
  onToggle,
  actions,
  children,
}: {
  fold: Fold;
  open: boolean;
  /** Pastille devant le titre (étape) ; sans elle, un chevron (options). */
  mark?: ReactNode;
  title: string;
  /** Résumé du volet replié (une ligne, rien de coupé au milieu d'un mot). */
  summary?: string;
  badge?: { text: string; tone: StepBadgeTone } | null;
  /** « Modifier » sur le volet replié (étape déjà validée). */
  editable?: boolean;
  onToggle: () => void;
  /** Bouton à droite de l'en-tête (aide), hors du bouton du volet. */
  actions?: ReactNode;
  children: ReactNode;
}) {
  const id = useId();
  return (
    <section data-fold={fold} data-step={fold} className="border-b border-line last:border-b-0">
      <div className="flex items-start gap-1 pr-2">
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={open}
          aria-controls={id}
          className="focus-inset flex min-h-8 min-w-0 flex-1 items-start gap-3 py-3 pr-2 pl-4 text-left transition-colors duration-(--gx-enter) hover:bg-hover/60"
        >
          <span className="flex h-5 shrink-0 items-center">{mark ?? <DisclosureChevron open={open} />}</span>
          <span className="min-w-0 flex-1">
            <span className={clsx("block text-ui leading-5 font-medium break-words", open ? "text-ink" : "text-ink-soft")}>{title}</span>
            <Presence show={!open && Boolean(summary)}>
              {summary && (
                <Motion origin="fade" className="mt-0.5 block text-meta leading-snug break-words text-ink-muted">
                  {summary}
                </Motion>
              )}
            </Presence>
          </span>
          <span className="flex h-5 shrink-0 items-center gap-2">
            <Presence show={Boolean(badge)}>
              {badge && (
                <Motion key={badge.text} origin="fade" className="inline-flex">
                  <Badge tone={badge.tone}>{badge.text}</Badge>
                </Motion>
              )}
            </Presence>
            <Presence show={!open && Boolean(editable) && !badge}>
              <Motion origin="fade" className="text-meta font-medium text-accent">
                {t("Modifier")}
              </Motion>
            </Presence>
          </span>
        </button>
        {actions && <span className="flex h-11 shrink-0 items-center">{actions}</span>}
      </div>
      <Disclosure open={open} id={id}>
        <div className="space-y-5 px-4 pt-1 pb-5">{children}</div>
      </Disclosure>
    </section>
  );
}

/** Intitulé d'un groupe des options facultatives (« Drones », « Environnement »…), avec son action à droite. */
export function OptionHeading({ children, actions }: { children: ReactNode; actions?: ReactNode }) {
  return (
    <div className="flex min-h-8 items-center gap-2">
      <h4 className="min-w-0 flex-1 text-ui leading-snug font-medium text-ink">{children}</h4>
      {actions}
    </div>
  );
}
